import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	OnChanges,
} from '@angular/core';
import LogRocket from 'logrocket';
import { TeranetService } from '@services/teranet/teranet.service';
import { MatterFileType, MatterLRO } from '@models/matter';
import {
	PersonClient,
	BusinessClient,
	ClientType,
	BaseClient,
} from '@models/clients';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { firstValueFrom, Observable } from 'rxjs';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ICON } from '@core/util/icon';
import { openCloseWithState } from '@core/animations/animations';
import { SnackbarService } from '@services/ui/snackbar.service';
import { UserProfileDispatch } from '@state/user-profile/dispatch';
import { TestHooks } from './search-by-owner.hooks';
import { BaseComponent } from '@core/base.component';
import { SearchPropertyOwner, PinsResult } from '@models/teranet';

interface VM {
	searchingPins: boolean;
	loading: boolean;
	firstName?: string;
	lastName?: string;
	selectedName?: string;
	owners?: SearchPropertyOwner[];
	pins?: PinsResult;
	error: string;
	submitted: boolean;
	showSearchSection?: boolean;
	quickSearchText?: string;
}

const defaultVM = {
	searchingPins: false,
	loading: false,
	firstName: '',
	lastName: '',
	selectedName: '',
	owners: null,
	pins: null,
	error: null,
	submitted: false,
	showSearchSection: true,
	quickSearchText: '',
};
@Component({
	selector: 'app-teranet-search-by-owner',
	templateUrl: './search-by-owner.component.html',
	styleUrls: ['./search-by-owner.component.scss'],
	animations: [openCloseWithState()],
	standalone: false,
})
export class SearchByOwnerComponent
	extends BaseComponent<VM>
	implements OnInit, OnChanges
{
	public clientTypes = ClientType;
	public ICON = ICON;
	testHooks = TestHooks;

	@Input() purchaseDeleted: boolean;
	@Input() isOverview = false;
	@Input() showSearch = true;
	@Output() searchCompletedEvent = new EventEmitter<boolean>();
	@Output() lroNotSelectedEvent = new EventEmitter();

	@Select(MatterClients.get_owners) owners$: Observable<
		BusinessClient[] | PersonClient[]
	>;

	@SelectSnapshot(SelectedMatter.LRO) lro: MatterLRO;
	@SelectSnapshot(SelectedMatter.id) saleId: string;
	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: string;

	constructor(
		private teranetService: TeranetService,
		private snackbar: SnackbarService,
		private userProfileDispatch: UserProfileDispatch,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.VM = {
			quickSearchText:
				this.matterFileType === MatterFileType.REFINANCE
					? 'of Existing Owner(s)'
					: 'From APS',
		};
	}

	ngOnChanges(): void {
		this.VM = {
			showSearchSection: this.showSearch,
		};
	}

	async searchOwners(): Promise<void> {
		this.VM = {
			pins: null,
			owners: [],
			error: null,
			submitted: true,
		};

		try {
			this.VM = {
				loading: true,
			};
			const result = await firstValueFrom(
				this.teranetService.searchPropertyOwners({
					lro: this.lro.selected.id,
					first_name: this.VM.firstName.trim(),
					last_name: this.VM.lastName.trim(),
				}),
			);

			if (result?.names) {
				this.VM = {
					owners: result.names,
				};

				if (this.VM.owners.length >= 1) {
					await this.searchProperties(
						result.names[0].first_name,
						result.names[0].last_name,
					);
					this.VM = {
						selectedName: `${result.names[0].first_name} ${result.names[0].last_name.replace(',', '')}`,
					};
				}
				this.lroNotSelectedEvent.emit(false);
				this.searchCompleted(true);
			}
		} catch (err) {
			LogRocket.log('Error searching owners', err);
			let friendlyError: string;
			if (!this.lro.selected?.id) {
				this.snackbar.openSnackBar({
					text: 'Error searching owners - Please Select an LRO',
					type: 'error',
				});
				friendlyError = 'Error searching owners - Please Select an LRO';
			} else
				friendlyError = this.teranetService.HandleError(
					'Error searching owners',
					err,
				);

			this.VM = {
				error: friendlyError,
			};
			this.searchCompleted(false);
		} finally {
			this.VM = {
				loading: false,
			};
		}
	}

	async searchProperties(_firstName: string, _lastName: string): Promise<void> {
		this.VM = {
			searchingPins: true,
		};

		const pinsResult = await firstValueFrom(
			this.teranetService.searchPropertyByOwners({
				lro: this.lro.selected.id,
				first_name: _firstName,
				last_name: _lastName,
			}),
		);

		this.VM = {
			searchingPins: false,
			pins: pinsResult ?? null,
		};
	}

	selectOwner(owner: BaseClient) {
		if (owner.type === ClientType.PERSON) {
			this.VM = {
				firstName: (owner as PersonClient).firstName,
				lastName: (owner as PersonClient).lastName,
			};
			this.searchOwners();
		} else {
			this.VM = {
				lastName: (owner as BusinessClient).name,
			};
		}
	}

	searchCompleted(value: boolean) {
		this.searchCompletedEvent.emit(value);
	}

	async onOwnerSelected(ev: SearchPropertyOwner): Promise<void> {
		await this.searchProperties(ev.first_name, ev.last_name);
		this.VM = {
			selectedName: `${ev.first_name} ${ev.last_name.replace(',', '')}`,
		};
	}
}
